import {PromptPool, TTSVoiceType} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {InAndroid} from "../../../../../Utils/Bridge/Bridge_Native.js";
import {E} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Row, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {RunInAction_Set} from "web-vcore";
import {ParseCustomPromptPool, PromptPoolTargetToTTSText} from "../../../../../Engine/LiveSession/Components/Visualization/VisualizationUtils.js";
import {store} from "../../../../../Store/index.js";
import {GetAudioFilesWithSubpath, GetCache} from "../../../../../Store/main/general/CacheState.js";
import {ShowToastAndLog} from "../../../../../Utils/Bridge/Bridge_Native/General.js";
import {SaveAudioDataToFile} from "../../../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {GenerateSpeechWithElevenLabs} from "../../../../../Utils/Services/ElevenLabs.js";
import {NarrateText} from "../../../../../Utils/Services/TTS.js";
import {GetStandardFilePathForTTSOptions_ForVisualization} from "../../../../../Utils/Services/FilePaths.js";
import {ErrToStr} from "../../../../../Utils/General/Errors.js";

export const TTSBakingStatus_Visualization = observer_mgl((props: {pool: PromptPool, enabled?: boolean})=>{
	const {pool, enabled} = props;
	const [bakingProgress, setBakingProgress] = React.useState({current: 0, total: 0});
	const isBaking = bakingProgress.total > 0;

	const targetPool = ParseCustomPromptPool(pool.customPoolStr).flatMap(r=>r.targets_full.map(t=>t.text));

	const GetFilePathForTarget = (target: string)=>{
		const textForTTS = PromptPoolTargetToTTSText(target, pool);
		return GetStandardFilePathForTTSOptions_ForVisualization({text: textForTTS, ttsVoiceType: pool.ttsVoiceType!, elevenLabsVoiceId: pool.elevenLabsVoiceId});
	};

	const unbaked = targetPool.filter(target=>{
		const filePath = GetFilePathForTarget(target);
		const baked = GetAudioFilesWithSubpath(filePath)[0];
		return !baked;
	});

	const BakeTarget = async(target: string)=>{
		const filePath = GetFilePathForTarget(target);
		const textForTTS = PromptPoolTargetToTTSText(target, pool);

		if (pool.ttsVoiceType == TTSVoiceType.androidTTS) {
			await NarrateText({
				text: textForTTS,
				finalize_lastStep: self=>{
					self.saveToFile_relFilePath = filePath;
				},
			});
		} else if (pool.ttsVoiceType == TTSVoiceType.elevenLabs) {
			const elevenLabsAPIKey = store.main.settings.elevenLabsAPIKey;
			if (!elevenLabsAPIKey) {
				ShowMessageBox({
					title: "ElevenLabs API Key Required",
					message: "Please set your ElevenLabs API key in Settings -> General before baking with ElevenLabs.",
				});
				throw new Error("ElevenLabs API key not set");
			}
			const audioData = await GenerateSpeechWithElevenLabs({
				text: textForTTS,
				voiceId: pool.elevenLabsVoiceId!,
				apiKey: elevenLabsAPIKey,
			});
			// Save the audio data to file using the bridge
			await SaveAudioDataToFile(audioData, filePath);
		}
	};

	const BakeTargets = async(limit: number)=>{
		const count = Math.min(limit, unbaked.length);
		setBakingProgress({current: 0, total: count});
		try {
			for (let i = 0; i < count; i++) {
				const target = unbaked[i];
				setBakingProgress({current: i + 1, total: count});
				await BakeTarget(target);
			}
			ShowToastAndLog(`Baked ${count} TTS messages.`);
			RunInAction_Set(()=>GetCache().audioFiles_updatedRequestedAt = Date.now());
		} catch (err) {
			ShowToastAndLog(`TTS baking failed: ${ErrToStr(err)}`);
		} finally {
			setBakingProgress({current: 0, total: 0});
		}
	};
	const bakeButtonsEnabled = enabled && unbaked.length > 0 && !isBaking && InAndroid(0); // only Android has storage mechanism atm

	return (
		<Row style={{flexWrap: "wrap", columnGap: 5, rowGap: 3}}>
			{targetPool.map((target, index)=>{
				const filePath = GetFilePathForTarget(target);
				const baked = GetCache().audioFiles.find(a=>a.filePath.includes(filePath));
				return <Text key={index} style={E(
					!baked && {color: "red"},
					baked && {color: "lightgreen"},
				)}>{target}</Text>
			})}
			<Button text="Bake all" enabled={bakeButtonsEnabled} onClick={()=>BakeTargets(10000)}/>
			<Button ml={5} text="Bake next 1" enabled={bakeButtonsEnabled} onClick={()=>BakeTargets(1)}/>
			<Button ml={5} text="Bake next 10" enabled={bakeButtonsEnabled} onClick={()=>BakeTargets(10)}/>
			{isBaking && <Text ml={5}>Baking {bakingProgress.current}/{bakingProgress.total}</Text>}
			<Button ml={5} mdIcon="refresh" title="Refresh bake statuses" enabled={enabled && !isBaking} onClick={()=>{
				RunInAction_Set(()=>GetCache().audioFiles_updatedRequestedAt = Date.now());
			}}/>
		</Row>
	);
});